// telas/Pronta.tsx - A dinâmica pronta (07_VOZ §4, §5 e §5b; D77, D104; P32 em D41; mockup, estado 3).
//
// As partes que a Dinâmica já gera (F10, D25): título, tempo, objetivos, passo a passo (com o áudio),
// dicas para todos os alunos (DUA), por que funciona (JiTT) e os materiais. "Salvar em PDF" é o único
// botão laranja (D77); "WhatsApp" e "Criar outra" são secundários. Depois, "Esta dinâmica ajudou?" (D104)
// e "Reportar um problema".

import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Chip,
  ConfirmDialog,
  IconClock,
  IconDownload,
  IconFlag,
  IconMessage,
  IconPause,
  IconRotateCcw,
  IconSend,
  IconSparkles,
  IconTarget,
  IconVolume,
  Notice,
  Tag,
  TextAreaField,
  Toast,
} from '@winieduca/ui';
import type { ResponseDinamica } from '../types/index';
import { hubUrl } from '../config/hub';
import { reportarProblema } from '../services/api';
import { enviarComentario, registrarEvento } from '../services/hubApi';
import { auth } from '../services/firebase';
import { acoes, comentarioVazio, feedback, feedbackNovos, pronta, prontaNovos, recebe, reportar, whatsapp } from '../content/textos';
import { paraWhatsapp, semMarcas, TextoGerado } from './TextoGerado';

const DATA = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Sao_Paulo' });

type EstadoAudio = 'parado' | 'tocando' | 'pausado';

function useAudio(url: string | undefined) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [estado, setEstado] = useState<EstadoAudio>('parado');
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    return () => {
      audio.current?.pause();
      audio.current = null;
    };
  }, [url]);

  function criar() {
    if (!url) return null;
    const a = new Audio(url);
    a.addEventListener('play', () => setEstado('tocando'));
    a.addEventListener('pause', () => setEstado((s) => (s === 'parado' ? s : 'pausado')));
    a.addEventListener('ended', () => setEstado('parado'));
    a.addEventListener('error', () => {
      setFalhou(true);
      setEstado('parado');
    });
    audio.current = a;
    return a;
  }

  function tocarOuPausar() {
    setFalhou(false);
    const a = audio.current ?? criar();
    if (!a) return;
    if (estado === 'tocando') a.pause();
    else void a.play().catch(() => setFalhou(true));
  }

  function doComeco() {
    setFalhou(false);
    const a = audio.current ?? criar();
    if (!a) return;
    a.currentTime = 0;
    void a.play().catch(() => setFalhou(true));
  }

  return { estado, falhou, tocarOuPausar, doComeco };
}

async function baixarImagem(url: string, nome: string): Promise<boolean> {
  try {
    const r = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!r.ok) return false;
    const blob = await r.blob();
    if (!blob.size) return false;
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = nome;
    document.body.appendChild(a);
    a.click();
    window.setTimeout(() => {
      a.remove();
      URL.revokeObjectURL(href);
    }, 100);
    return true;
  } catch {
    return false;
  }
}

export function Pronta({ resultado, atividadeId, onCriarOutra }: { resultado: ResponseDinamica; atividadeId?: string; onCriarOutra: () => void }) {
  const d = resultado.dinamica;
  const titulo = semMarcas(d.titulo);
  const audio = useAudio(resultado.audioInstrucoes?.urlAudio);
  const [pdfFalhou, setPdfFalhou] = useState(false);
  const [falhaNoMaterial, setFalhaNoMaterial] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // Sem nova aba (D30): o PDF sai da impressão da própria página ("Salvar como PDF"), igual em qualquer
  // navegador, inclusive no Chrome do Android. O CSS de impressão (telas.css) deixa só a dinâmica; o título
  // da aba vira o nome do arquivo e volta depois.
  function salvarPdf() {
    setPdfFalhou(false);
    const anterior = document.title;
    const voltar = () => {
      document.title = anterior;
      window.removeEventListener('afterprint', voltar);
    };
    try {
      document.title = titulo;
      window.addEventListener('afterprint', voltar);
      window.setTimeout(voltar, 60000);
      window.print();
      if (atividadeId) registrarEvento({ event: 'dinamica_pdf', props: { atividade_id: atividadeId } });
    } catch {
      voltar();
      setPdfFalhou(true);
    }
  }

  function compartilhar() {
    const texto = whatsapp.mensagem(titulo, d.tempo, d.instrucoes.map((i) => paraWhatsapp(i.texto)), hubUrl('/'));
    // No celular, o endereço abre o aplicativo do WhatsApp; no computador, o WhatsApp Web. A Dinâmica fica aberta.
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener,noreferrer');
    if (atividadeId) registrarEvento({ event: 'dinamica_whatsapp', props: { atividade_id: atividadeId } });
  }

  async function baixar(id: string, url: string, titulo: string, tipo?: string) {
    setFalhaNoMaterial(null);
    const nome = `${(titulo || 'material').replace(/[^a-z0-9]+/gi, '_')}.${tipo || 'png'}`;
    if (!(await baixarImagem(url, nome))) setFalhaNoMaterial(id);
  }

  return (
    <article className="din-pronta" aria-labelledby="pronta-titulo">
      <div className="din-pronta__texto">
        <header className="din-pronta__topo">
          <Tag tone="success">{pronta.selo}</Tag>
          <h1 id="pronta-titulo" className="we-display din-titulo" tabIndex={-1}>
            {titulo}
          </h1>
          <p className="din-pronta__meta">
            <IconClock />
            <span>{d.tempo}</span>
          </p>
          {d.objetivosPedagogicos.length > 0 && (
            <div className="din-pronta__objetivos">
              <span className="din-pronta__rotulo">{prontaNovos.objetivos}</span>
              <ul className="din-objetivos">
                {d.objetivosPedagogicos.map((o) => (
                  <li key={o} className="din-objetivos__item">
                    <IconTarget className="din-objetivos__icone" />
                    <span>
                      <TextoGerado texto={o} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </header>

        <section className="din-secao" aria-labelledby="secao-passo">
          <div className="din-secao__cabeca">
            <h2 id="secao-passo" className="we-h2">
              {pronta.passo}
            </h2>
            {resultado.audioInstrucoes?.urlAudio && (
              <div className="din-audio">
                <Button variant="quiet" icon={audio.estado === 'tocando' ? IconPause : IconVolume} onClick={audio.tocarOuPausar}>
                  {audio.estado === 'tocando' ? prontaNovos.pausar : prontaNovos.ouvir}
                </Button>
                {audio.estado !== 'parado' && (
                  <Button variant="quiet" icon={IconRotateCcw} onClick={audio.doComeco}>
                    {prontaNovos.ouvirDoComeco}
                  </Button>
                )}
              </div>
            )}
          </div>
          {audio.falhou && <Notice tone="error" live>{prontaNovos.audioFalha}</Notice>}
          <ol className="din-passos">
            {d.instrucoes.map((i) => (
              <li key={i.passo} className="din-passos__item">
                <span className="din-passos__num" aria-hidden="true">
                  {i.passo}
                </span>
                <p>
                  <TextoGerado texto={i.texto} />
                </p>
              </li>
            ))}
          </ol>
        </section>

        {d.dicasInclusaoDua.length > 0 && (
          <section className="din-secao" aria-labelledby="secao-dua">
            <h2 id="secao-dua" className="we-h2">
              {pronta.dua}
            </h2>
            <ul className="din-dicas">
              {d.dicasInclusaoDua.map((dica, idx) => (
                <li key={`${dica.condicao}-${idx}`} className="din-dicas__item">
                  <strong>{semMarcas(dica.condicao)}</strong>
                  <p>
                    <TextoGerado texto={dica.script} />
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="din-secao" aria-labelledby="secao-porque">
          <h2 id="secao-porque" className="we-h2">
            {recebe.porque}
          </h2>
          <p className="din-porque">
            <TextoGerado texto={d.porquePedagogicoJitt} />
          </p>
        </section>

        {resultado.materiaisGerados?.length > 0 && (
          <section className="din-secao" aria-labelledby="secao-materiais">
            <h2 id="secao-materiais" className="we-h2">
              {prontaNovos.materiais}
            </h2>
            <ul className="din-materiais">
              {resultado.materiaisGerados.map((m, idx) => {
                const id = m.id || String(idx);
                return (
                  <li key={id} className="din-materiais__item">
                    <img className="din-materiais__img" src={m.urlPreview || m.urlDownload} alt={m.descricao || m.titulo} loading="lazy" />
                    <span className="din-materiais__titulo">{m.titulo}</span>
                    <Button icon={IconDownload} onClick={() => void baixar(id, m.urlDownload, m.titulo, m.tipo)}>
                      {prontaNovos.baixar}
                    </Button>
                    {falhaNoMaterial === id && (
                      <p className="din-erro" role="alert">
                        {prontaNovos.baixarFalha}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>

      {/* No celular, as ações ficam presas embaixo, acima da barra de seções (DESIGN_SYSTEM §6, "Resultado");
          no computador, numa coluna ao lado da dinâmica, sempre à vista. */}
      <div className="din-pronta__lado">
        <div className="din-acoes">
          <Notice tone="info">{prontaNovos.ia}</Notice>
          {pdfFalhou && <Notice tone="error" live>{prontaNovos.pdfFalha}</Notice>}
        </div>
        <div className="din-barra din-barra--pronta">
          <Button variant="primary" icon={IconDownload} onClick={salvarPdf}>
            {acoes.pdf}
          </Button>
          <Button icon={IconMessage} onClick={compartilhar}>
            {acoes.whatsapp}
          </Button>
          <Button icon={IconSparkles} onClick={onCriarOutra}>
            {acoes.outra}
          </Button>
        </div>
      </div>

      {/* Só no papel (o PDF): de onde veio a dinâmica e quando. */}
      <p className="din-impresso">{prontaNovos.criadaEm(DATA.format(new Date()))}</p>

      <Feedback atividadeId={atividadeId} />
      <Reportar geracaoId={resultado.geracaoId || atividadeId} aoEnviar={() => setAviso(reportar.enviado)} />
      <Toast open={Boolean(aviso)} onClose={() => setAviso(null)}>
        {aviso}
      </Toast>
    </article>
  );
}

function Feedback({ atividadeId }: { atividadeId?: string }) {
  const [resposta, setResposta] = useState<boolean | null>(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const [erro, setErro] = useState<string | undefined>();

  function responder(ajudou: boolean) {
    setResposta(ajudou);
    if (atividadeId) registrarEvento({ event: 'dinamica_feedback', props: { atividade_id: atividadeId, ajudou } });
  }

  async function enviar() {
    const limpo = texto.trim();
    if (limpo.length < 3) {
      setErro(comentarioVazio.texto);
      return;
    }
    setEnviando(true);
    setFalhou(false);
    const ok = await enviarComentario(limpo, atividadeId);
    setEnviando(false);
    if (ok) setEnviado(true);
    else setFalhou(true);
  }

  return (
    <section className="din-feedback" aria-labelledby="feedback-pergunta">
      <h2 id="feedback-pergunta" className="din-feedback__pergunta">
        {feedback.pergunta}
      </h2>
      <div className="din-feedback__opcoes" role="group" aria-labelledby="feedback-pergunta">
        <Chip pressed={resposta === true} onPressedChange={() => responder(true)}>
          {feedback.sim}
        </Chip>
        <Chip pressed={resposta === false} onPressedChange={() => responder(false)}>
          {feedback.nao}
        </Chip>
      </div>
      {resposta !== null && (
        <div className="din-feedback__mais" aria-live="polite">
          <p className="din-feedback__obrigado">{feedbackNovos.obrigado}</p>
          {!enviado && (
            <>
              <TextAreaField
                label={feedback.contar}
                rows={3}
                maxLength={2000}
                value={texto}
                error={erro}
                onChange={(e) => {
                  setTexto(e.target.value);
                  if (erro) setErro(undefined);
                }}
              />
              {falhou && <Notice tone="error" live>{reportar.falha}</Notice>}
              <Button icon={IconSend} loading={enviando} onClick={() => void enviar()} className="din-feedback__enviar">
                {feedbackNovos.enviar}
              </Button>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function Reportar({ geracaoId, aoEnviar }: { geracaoId?: string; aoEnviar: () => void }) {
  const [aberto, setAberto] = useState(false);
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | undefined>();
  const [falhou, setFalhou] = useState(false);

  async function enviar() {
    const limpo = texto.trim();
    if (!limpo) {
      setErro(reportar.vazio);
      return;
    }
    setFalhou(false);
    try {
      await reportarProblema({
        geracaoId: geracaoId || `geracao-${Date.now()}`,
        email: auth.currentUser?.email ?? '',
        descricao: limpo,
      });
      setAberto(false);
      setTexto('');
      aoEnviar();
    } catch {
      setFalhou(true);
    }
  }

  return (
    <div className="din-reportar">
      <Button variant="quiet" icon={IconFlag} onClick={() => setAberto(true)}>
        {reportar.abrir}
      </Button>
      <ConfirmDialog
        open={aberto}
        title={reportar.titulo}
        confirmLabel={reportar.enviar}
        cancelLabel={reportar.cancelar}
        onConfirm={() => void enviar()}
        onCancel={() => {
          setAberto(false);
          setErro(undefined);
          setFalhou(false);
        }}
      >
        <TextAreaField
          label={reportar.campo}
          help={reportar.ajuda}
          error={erro}
          rows={4}
          maxLength={2000}
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value);
            if (erro) setErro(undefined);
          }}
        />
        {falhou && <Notice tone="error" live>{reportar.falha}</Notice>}
      </ConfirmDialog>
    </div>
  );
}
